// components/CommentSection/CommentSection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function avatar(username)      { return (username || '?')[0].toUpperCase(); }

const AVATAR_COLORS = {
  A:'#f97316',B:'#8b5cf6',C:'#06b6d4',D:'#10b981',E:'#f59e0b',
  F:'#ef4444',G:'#3b82f6',H:'#ec4899',I:'#14b8a6',J:'#a855f7',
  default:'#6b7280',
};
function avatarColor(username) {
  const c = (username || '?')[0].toUpperCase();
  return AVATAR_COLORS[c] || AVATAR_COLORS.default;
}

// ── Single Reply ─────────────────────────────────────────────────────────────
function Reply({ reply, currentUserId, onDelete, deletingId }) {
  const isOwn = currentUserId && reply.userId?.toString() === currentUserId?.toString();
  return (
    <div className="cs-reply">
      <div className="cs-avatar cs-avatar-sm" style={{ background: avatarColor(reply.username) }}>
        {avatar(reply.username)}
      </div>
      <div className="cs-comment-body">
        <div className="cs-comment-meta">
          <span className="cs-username">{reply.username}</span>
          <span className="cs-time">{timeAgo(reply.createdAt)}</span>
          {isOwn && (
            <button
              className="cs-delete-btn"
              onClick={() => onDelete(reply._id)}
              disabled={deletingId === reply._id}
              title="Delete reply"
            >
              {deletingId === reply._id ? '…' : '✕'}
            </button>
          )}
        </div>
        <p className="cs-comment-text">{reply.text}</p>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function CommentSection({ cardId, isLoggedIn }) {
  const [comments,    setComments]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [text,        setText]        = useState('');
  const [posting,     setPosting]     = useState(false);
  const [error,       setError]       = useState('');
  const [open,        setOpen]        = useState(false);
  const [deletingId,  setDeletingId]  = useState(null);

  // Reply state
  const [replyingTo,  setReplyingTo]  = useState(null); // commentId being replied to
  const [replyText,   setReplyText]   = useState('');
  const [postingReply,setPostingReply]= useState(false);
  const [replyError,  setReplyError]  = useState('');

  let currentUserId = null;
  try {
    const stored = localStorage.getItem('vidyacards_user');
    if (stored) {
      const u = JSON.parse(stored);
      currentUserId = u._id || u.id;
    }
  } catch (_) {}

  const fetchComments = useCallback(async () => {
    if (!cardId) return;
    try {
      const res = await api.get(`/comments/card/${cardId}`);
      setComments(res.data.comments || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, [cardId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  // ── Post top-level comment ─────────────────────────────────────────────────
  const handlePost = async () => {
    const trimmed = text.trim();
    if (!trimmed || posting) return;
    if (trimmed.length > 500) { setError('Max 500 characters'); return; }
    setPosting(true); setError('');
    try {
      const res = await api.post(`/comments/card/${cardId}`, { text: trimmed });
      setComments(prev => [res.data.comment, ...prev]);
      setText('');
      setOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post');
    } finally { setPosting(false); }
  };

  // ── Post reply ─────────────────────────────────────────────────────────────
  const handlePostReply = async (commentId) => {
    const trimmed = replyText.trim();
    if (!trimmed || postingReply) return;
    if (trimmed.length > 500) { setReplyError('Max 500 characters'); return; }
    setPostingReply(true); setReplyError('');
    try {
      const res = await api.post(`/comments/${commentId}/reply`, { text: trimmed });
      // Merge the new reply into the correct comment
      setComments(prev => prev.map(c =>
        c._id === commentId
          ? { ...c, replies: [...(c.replies || []), res.data.reply] }
          : c
      ));
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Failed to post reply');
    } finally { setPostingReply(false); }
  };

  // ── Delete comment or reply ────────────────────────────────────────────────
  const handleDelete = async (commentId) => {
    if (deletingId) return;
    setDeletingId(commentId);
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c._id !== commentId));
    } catch (_) {}
    finally { setDeletingId(null); }
  };

  const handleDeleteReply = async (replyId, parentCommentId) => {
    if (deletingId) return;
    setDeletingId(replyId);
    try {
      await api.delete(`/comments/${replyId}`);
      setComments(prev => prev.map(c =>
        c._id === parentCommentId
          ? { ...c, replies: (c.replies || []).filter(r => r._id !== replyId) }
          : c
      ));
    } catch (_) {}
    finally { setDeletingId(null); }
  };

  const count = comments.length;

  return (
    <div className="cs-root" onClick={e => e.stopPropagation()}>

      {/* ── Toggle ── */}
      <button className="cs-toggle" onClick={() => setOpen(v => !v)}>
        <span className="cs-toggle-left">
          <span className="cs-icon">💬</span>
          <span className="cs-label">
            {loading ? 'Comments' : count === 0 ? 'No comments yet' : `${count} comment${count !== 1 ? 's' : ''}`}
          </span>
        </span>
        <span className="cs-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="cs-body">

          {/* ── Post input ── */}
          {isLoggedIn ? (
            <div className="cs-input-row">
              <textarea
                className="cs-textarea"
                placeholder="Share a tip, doubt, or note…"
                value={text}
                onChange={e => { setText(e.target.value); setError(''); }}
                maxLength={500}
                rows={2}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePost(); }}
              />
              <div className="cs-input-footer">
                {error && <span className="cs-error">{error}</span>}
                <span className="cs-charcount" style={{ color: text.length > 450 ? 'var(--saffron)' : 'var(--text-3)' }}>
                  {text.length}/500
                </span>
                <button
                  className="btn btn-primary cs-post-btn"
                  onClick={handlePost}
                  disabled={posting || !text.trim()}
                >
                  {posting ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Post'}
                </button>
              </div>
            </div>
          ) : (
            <div className="cs-login-prompt">
              <Link to="/login" className="btn btn-secondary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}>
                Log in to comment
              </Link>
              <span style={{ color: 'var(--text-3)', fontSize: '0.82rem' }}>Comments are visible to everyone</span>
            </div>
          )}

          {/* ── Comment list ── */}
          <div className="cs-list">
            {loading && (
              <div className="cs-empty">
                <div className="spinner" style={{ width: 20, height: 20 }} />
              </div>
            )}
            {!loading && comments.length === 0 && (
              <div className="cs-empty">
                <span style={{ fontSize: '1.5rem' }}>💬</span>
                <span>No comments yet — be the first!</span>
              </div>
            )}

            {comments.map(c => {
              const isOwn    = currentUserId && c.userId?.toString() === currentUserId?.toString();
              const isReplying = replyingTo === c._id;
              const replies  = c.replies || [];

              return (
                <div key={c._id} className="cs-comment-thread">

                  {/* Parent comment */}
                  <div className="cs-comment">
                    <div className="cs-avatar" style={{ background: avatarColor(c.username) }}>
                      {avatar(c.username)}
                    </div>
                    <div className="cs-comment-body">
                      <div className="cs-comment-meta">
                        <span className="cs-username">{c.username}</span>
                        <span className="cs-time">{timeAgo(c.createdAt)}</span>
                        {isOwn && (
                          <button
                            className="cs-delete-btn"
                            onClick={() => handleDelete(c._id)}
                            disabled={deletingId === c._id}
                            title="Delete comment"
                          >
                            {deletingId === c._id ? '…' : '✕'}
                          </button>
                        )}
                      </div>
                      <p className="cs-comment-text">{c.text}</p>

                      {/* Reply button */}
                      {isLoggedIn && (
                        <button
                          className="cs-reply-btn"
                          onClick={() => {
                            setReplyingTo(isReplying ? null : c._id);
                            setReplyText('');
                            setReplyError('');
                          }}
                        >
                          {isReplying ? 'Cancel' : `↩ Reply${replies.length > 0 ? ` · ${replies.length}` : ''}`}
                        </button>
                      )}
                      {!isLoggedIn && replies.length > 0 && (
                        <span className="cs-reply-count">↩ {replies.length} {replies.length === 1 ? 'reply' : 'replies'}</span>
                      )}
                    </div>
                  </div>

                  {/* Replies */}
                  {replies.length > 0 && (
                    <div className="cs-replies">
                      {replies.map(r => (
                        <Reply
                          key={r._id}
                          reply={r}
                          currentUserId={currentUserId}
                          onDelete={(replyId) => handleDeleteReply(replyId, c._id)}
                          deletingId={deletingId}
                        />
                      ))}
                    </div>
                  )}

                  {/* Inline reply box */}
                  {isReplying && (
                    <div className="cs-reply-box">
                      <textarea
                        className="cs-textarea cs-reply-textarea"
                        placeholder={`Replying to ${c.username}…`}
                        value={replyText}
                        onChange={e => { setReplyText(e.target.value); setReplyError(''); }}
                        maxLength={500}
                        rows={2}
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePostReply(c._id); }}
                      />
                      <div className="cs-input-footer">
                        {replyError && <span className="cs-error">{replyError}</span>}
                        <span className="cs-charcount" style={{ color: replyText.length > 450 ? 'var(--saffron)' : 'var(--text-3)' }}>
                          {replyText.length}/500
                        </span>
                        <button
                          className="btn btn-primary cs-post-btn"
                          onClick={() => handlePostReply(c._id)}
                          disabled={postingReply || !replyText.trim()}
                        >
                          {postingReply ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Reply'}
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>

        </div>
      )}

      <style>{`
        .cs-root {
          margin-top: 0.75rem;
          border: 1px solid var(--border);
          border-radius: var(--radius-lg, 12px);
          background: var(--bg-card);
          overflow: hidden;
        }
        .cs-toggle {
          width: 100%;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0.75rem 1rem;
          background: none; border: none; cursor: pointer;
          color: var(--text-2); font-size: 0.9rem;
          transition: background 0.15s;
        }
        .cs-toggle:hover { background: rgba(0,0,0,0.04); }
        .cs-toggle-left { display: flex; align-items: center; gap: 0.5rem; }
        .cs-icon  { font-size: 1rem; }
        .cs-label { font-weight: 500; color: var(--text); }
        .cs-chevron { font-size: 0.7rem; color: var(--text-3); }

        .cs-body { border-top: 1px solid var(--border); }

        .cs-input-row { padding: 0.75rem 1rem 0; }
        .cs-textarea {
          width: 100%; box-sizing: border-box;
          background: var(--bg, #0e0e10);
          border: 1px solid var(--border); border-radius: 8px;
          color: var(--text); font-family: var(--font-body, sans-serif);
          font-size: 0.875rem; line-height: 1.5;
          padding: 0.6rem 0.75rem; resize: none; outline: none;
          transition: border-color 0.15s;
        }
        .cs-textarea:focus  { border-color: var(--saffron, #f5a623); }
        .cs-textarea::placeholder { color: var(--text-3); }

        .cs-input-footer {
          display: flex; align-items: center;
          justify-content: flex-end; gap: 0.5rem;
          padding: 0.4rem 0 0.6rem;
        }
        .cs-charcount { font-size: 0.75rem; }
        .cs-error     { font-size: 0.8rem; color: var(--red, #ef4444); flex: 1; }
        .cs-post-btn  { padding: 0.35rem 0.9rem !important; font-size: 0.83rem !important; }

        .cs-login-prompt {
          display: flex; align-items: center; gap: 0.75rem;
          padding: 0.75rem 1rem; border-bottom: 1px solid var(--border);
        }

        .cs-list {
          max-height: 480px; overflow-y: auto;
          padding: 0.5rem 1rem 0.75rem;
          display: flex; flex-direction: column; gap: 0;
        }
        .cs-empty {
          display: flex; flex-direction: column; align-items: center;
          gap: 0.4rem; padding: 1.5rem 0;
          color: var(--text-3); font-size: 0.85rem; text-align: center;
        }

        /* ── Comment thread ── */
        .cs-comment-thread {
          padding: 0.75rem 0;
          border-bottom: 1px solid var(--border);
        }
        .cs-comment-thread:last-child { border-bottom: none; }

        .cs-comment {
          display: flex; gap: 0.65rem; align-items: flex-start;
        }
        .cs-avatar {
          width: 30px; height: 30px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem; font-weight: 700; color: #fff; flex-shrink: 0;
        }
        .cs-avatar-sm { width: 24px; height: 24px; font-size: 0.65rem; }

        .cs-comment-body  { flex: 1; min-width: 0; }
        .cs-comment-meta  {
          display: flex; align-items: center; gap: 0.45rem;
          margin-bottom: 0.2rem;
        }
        .cs-username { font-size: 0.8rem; font-weight: 600; color: var(--text); }
        .cs-time     { font-size: 0.75rem; color: var(--text-3); }
        .cs-delete-btn {
          margin-left: auto; background: none; border: none;
          color: var(--text-3); cursor: pointer; font-size: 0.7rem;
          padding: 0 0.25rem; opacity: 0.6; transition: opacity 0.15s;
        }
        .cs-delete-btn:hover { opacity: 1; color: var(--red, #ef4444); }
        .cs-comment-text {
          font-size: 0.875rem; line-height: 1.55; color: var(--text-2);
          margin: 0; word-break: break-word; white-space: pre-wrap;
        }

        /* ── Reply button ── */
        .cs-reply-btn {
          background: none; border: none; cursor: pointer;
          color: var(--text-3); font-size: 0.75rem;
          padding: 0.25rem 0; margin-top: 0.25rem;
          transition: color 0.12s; font-family: var(--font-body);
        }
        .cs-reply-btn:hover { color: var(--saffron); }
        .cs-reply-count {
          font-size: 0.75rem; color: var(--text-3); margin-top: 0.25rem;
          display: block;
        }

        /* ── Nested replies ── */
        .cs-replies {
          margin-left: 38px;
          margin-top: 0.5rem;
          display: flex; flex-direction: column; gap: 0.5rem;
          border-left: 2px solid var(--border);
          padding-left: 0.75rem;
        }
        .cs-reply {
          display: flex; gap: 0.5rem; align-items: flex-start;
        }

        /* ── Inline reply box ── */
        .cs-reply-box {
          margin-left: 38px;
          margin-top: 0.5rem;
          border-left: 2px solid var(--saffron);
          padding-left: 0.75rem;
        }
        .cs-reply-textarea { font-size: 0.825rem !important; }
      `}</style>
    </div>
  );
}